import React from 'react';
import { Helmet } from 'react-helmet';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { 
  FaArrowRight, 
  FaCloud, 
  FaCheck, 
  FaAws, 
  FaMicrosoft, 
  FaGoogle,
  FaDatabase,
  FaShieldAlt,
  FaRocket,
  FaChartLine,
  FaServer,
  FaCode,
  FaClock,
  FaUsers,
  FaStar,
  FaQuoteLeft,
  FaSync,
  FaExpand,
  FaLock
} from 'react-icons/fa';
import { SiDocker, SiKubernetes, SiTerraform, SiJenkins, SiAnsible, SiGitlab, SiAmazonecs, SiRedis, SiAmazon, SiGoogle } from 'react-icons/si';
import { serviceSeoData, seoImage } from '../../config/pageSeoConfig';
import './CloudSolutions.scss';

const CloudSolutions: React.FC = () => {
  // SEO constants from config
  const seoTitle = serviceSeoData.cloudSolutions.title;
  const seoDescription = serviceSeoData.cloudSolutions.description;
  const seoKeywords = serviceSeoData.cloudSolutions.keywords;
  const seoUrl = serviceSeoData.cloudSolutions.url;
  const schemaData = {
    "@context": "https://schema.org",
    "@type": "Service",
    "name": "Cloud Solutions & Infrastructure Services",
    "description": seoDescription,
    "provider": {
      "@type": "Organization",
      "name": "App Developer Pro",
      "url": "https://appdeveloperpro.com",
      "logo": {
        "@type": "ImageObject",
        "url": "https://appdeveloperpro.com/images/team-at-work-app-developer-pro.jpg"
      }
    },
    "areaServed": "Worldwide",
    "serviceType": "Cloud Solutions, Cloud Migration, AWS, Azure, Google Cloud, DevOps, Security, Disaster Recovery, Cost Optimization, 2025",
    "url": seoUrl,
    "image": seoImage
  };
  const features = [
    {
      icon: FaCloud,
      title: 'Cloud Migration',
      description: 'Seamlessly migrate your applications and data to the cloud with zero downtime'
    },
    {
      icon: FaExpand,
      title: 'Auto Scaling',
      description: 'Dynamic resource allocation that scales with your business demands'
    },
    {
      icon: FaDatabase,
      title: 'Cloud Storage',
      description: 'Secure, scalable cloud storage solutions with automated backups'
    },
    {
      icon: FaShieldAlt,
      title: 'Security & Compliance',
      description: 'Enterprise-grade security with industry compliance standards'
    },
    {
      icon: FaSync,
      title: 'Disaster Recovery',
      description: 'Robust backup and disaster recovery solutions for business continuity'
    },
    {
      icon: FaChartLine,
      title: 'Cost Optimization',
      description: 'Intelligent resource management to minimize cloud infrastructure costs'
    }
  ];

  const platforms = [
    { icon: FaAws, name: 'AWS', color: '#FF9900' },
    { icon: FaMicrosoft, name: 'Azure', color: '#0078D4' },
    { icon: FaGoogle, name: 'Google Cloud', color: '#4285F4' },
    { icon: SiDocker, name: 'Docker', color: '#2496ED' },
    { icon: SiKubernetes, name: 'Kubernetes', color: '#326CE5' },
    { icon: SiTerraform, name: 'Terraform', color: '#623CE4' },
    { icon: SiJenkins, name: 'Jenkins', color: '#D33833' },
    { icon: FaServer, name: 'Serverless', color: '#FF6B35' },
    { icon: SiAnsible, name: 'Ansible', color: '#EE0000' },
    { icon: SiGitlab, name: 'GitLab CI', color: '#FC6D26' },
    { icon: SiAmazonecs, name: 'Amazon ECS', color: '#FF9900' },
    { icon: SiRedis, name: 'Redis', color: '#DC382D' }
  ];

  const benefits = [
    {
      icon: FaRocket,
      title: 'Improved Performance',
      description: 'Faster load times and better user experience with cloud infrastructure'
    },
    {
      icon: FaLock,
      title: 'Enhanced Security',
      description: 'Advanced security features and compliance with industry standards'
    },
    {
      icon: FaChartLine,
      title: 'Cost Efficiency',
      description: 'Pay only for what you use with optimized cloud resource management'
    }
  ];

  const process = [
    {
      step: '01',
      title: 'Assessment & Planning',
      description: 'Analyze current infrastructure and create a comprehensive cloud strategy'
    },
    {
      step: '02',
      title: 'Architecture Design',
      description: 'Design scalable cloud architecture tailored to your business needs'
    },
    {
      step: '03',
      title: 'Migration & Deployment',
      description: 'Execute seamless migration with minimal disruption to operations'
    },
    {
      step: '04',
      title: 'Optimization & Support',
      description: 'Continuous monitoring and optimization for peak performance'
    }
  ];

  const testimonials = [
    {
      text: "The cloud migration was flawless. Our applications are now 50% faster and our operational costs have decreased significantly.",
      author: "Maria Rodriguez",
      company: "Tech Enterprise",
      rating: 5
    },
    {
      text: "Their expertise in AWS and cloud security gave us the confidence to fully embrace cloud computing. Excellent service!",
      author: "James Wilson",
      company: "Financial Services",
      rating: 5
    }
  ];

  return (
    <>
      <Helmet>
        <title>{seoTitle}</title>
        <meta name="description" content={seoDescription} />
        <meta name="keywords" content="cloud solutions, cloud migration, aws, azure, google cloud, devops, security, disaster recovery, cost optimization, cloud infrastructure, 2025" />
        <meta name="robots" content="index, follow" />
        <link rel="canonical" href={seoUrl} />
        {/* Open Graph */}
        <meta property="og:type" content="website" />
        <meta property="og:title" content={seoTitle} />
        <meta property="og:description" content={seoDescription} />
        <meta property="og:url" content={seoUrl} />
        <meta property="og:image" content={seoImage} />
        <meta property="og:site_name" content="App Developer Pro" />
        {/* Twitter Card */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={seoTitle} />
        <meta name="twitter:description" content={seoDescription} />
        <meta name="twitter:image" content={seoImage} />
        <meta name="twitter:site" content="@appdeveloperpro" />
        {/* Schema.org Markup */}
        <script type="application/ld+json">{JSON.stringify(schemaData)}</script>
      </Helmet>
      <div className="cloud-solutions">
      {/* Hero Section */}
      <section className="cloud-solutions__hero">
        <div className="cloud-solutions__hero-content">
          <motion.div
            className="cloud-solutions__hero-text"
            initial={{ opacity: 0, y: 50 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            <h1 className="cloud-solutions__hero-title">
              Scalable <span className="cloud-solutions__hero-highlight">Cloud Solutions</span> & Infrastructure
            </h1>
            <p className="cloud-solutions__hero-subtitle">
              Transform your business with powerful cloud infrastructure. From migration to optimization, we provide comprehensive cloud solutions that scale with your growth and reduce operational costs.
            </p>
            <div className="cloud-solutions__hero-buttons">
              <Link to="/contact" className="cloud-solutions__hero-btn cloud-solutions__hero-btn--primary">
                Get Cloud Assessment <FaArrowRight />
              </Link>
              <Link to="/portfolio" className="cloud-solutions__hero-btn cloud-solutions__hero-btn--secondary">
                View Case Studies
              </Link>
            </div>
          </motion.div>
          <motion.div
            className="cloud-solutions__hero-visual"
            initial={{ opacity: 0, x: 50 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8, delay: 0.2 }}
          >
            <div className="cloud-solutions__hero-cloud">
              <div className="cloud-solutions__hero-cloud-providers">
                <div className="cloud-solutions__hero-provider cloud-solutions__hero-provider--aws">
                  <SiAmazon />
                  <span>AWS</span>
                </div>
                <div className="cloud-solutions__hero-provider cloud-solutions__hero-provider--azure">
                  <FaMicrosoft />
                  <span>Azure</span>
                </div>
                <div className="cloud-solutions__hero-provider cloud-solutions__hero-provider--gcp">
                  <SiGoogle />
                  <span>Google Cloud</span>
                </div>
              </div>
              <div className="cloud-solutions__hero-infrastructure">
                <div className="cloud-solutions__hero-layer">
                  <FaServer />
                  <span>Infrastructure</span>
                </div>
                <div className="cloud-solutions__hero-layer">
                  <FaDatabase />
                  <span>Data Storage</span>
                </div>
                <div className="cloud-solutions__hero-layer">
                  <FaShieldAlt />
                  <span>Security</span>
                </div>
                <div className="cloud-solutions__hero-layer">
                  <FaChartLine />
                  <span>Analytics</span>
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Features Section */}
      <section className="cloud-solutions__features">
        <div className="cloud-solutions__section-header">
          <motion.h2
            className="cloud-solutions__section-title"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
          >
            Our Cloud Services
          </motion.h2>
          <motion.p
            className="cloud-solutions__section-subtitle"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.1 }}
            viewport={{ once: true }}
          >
            Comprehensive cloud solutions for modern businesses
          </motion.p>
        </div>
        <div className="cloud-solutions__features-grid">
          {features.map((feature, index) => {
            const FeatureIcon = feature.icon;
            return (
              <motion.div
                key={feature.title}
                className="cloud-solutions__feature-card"
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                viewport={{ once: true }}
                whileHover={{ y: -5 }}
              >
                <div className="cloud-solutions__feature-header">
                  <div className="cloud-solutions__feature-icon">
                    <FeatureIcon />
                  </div>
                  <h3 className="cloud-solutions__feature-title">{feature.title}</h3>
                </div>
                <p className="cloud-solutions__feature-description">{feature.description}</p>
              </motion.div>
            );
          })}
        </div>
      </section>

      {/* Platforms Section */}
      <section className="cloud-solutions__platforms">
        <motion.div
          className="cloud-solutions__section-header"
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          viewport={{ once: true }}
        >
          <h2 className="cloud-solutions__section-title">Cloud Platforms & Technologies</h2>
          <p className="cloud-solutions__section-subtitle">
            Expert implementation across all major cloud platforms
          </p>
        </motion.div>
        <div className="cloud-solutions__platforms-grid">
          {platforms.map((platform, index) => {
            const PlatformIcon = platform.icon;
            return (
              <motion.div
                key={platform.name}
                className="cloud-solutions__platform-item"
                initial={{ opacity: 0, scale: 0.5 }}
                whileInView={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                viewport={{ once: true }}
                whileHover={{ scale: 1.1, y: -5 }}
              >
                <div 
                  className="cloud-solutions__platform-icon"
                  style={{ '--platform-color': platform.color } as React.CSSProperties}
                >
                  <PlatformIcon />
                </div>
                <span className="cloud-solutions__platform-name">{platform.name}</span>
              </motion.div>
            );
          })}
        </div>
      </section>

      {/* Benefits Section */}
      <section className="cloud-solutions__benefits">
        <div className="cloud-solutions__section-header">
          <motion.h2
            className="cloud-solutions__section-title"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
          >
            Why Choose Our Cloud Solutions
          </motion.h2>
        </div>
        <div className="cloud-solutions__benefits-grid">
          {benefits.map((benefit, index) => {
            const BenefitIcon = benefit.icon;
            return (
              <motion.div
                key={benefit.title}
                className="cloud-solutions__benefit-card"
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                viewport={{ once: true }}
              >
                <div className="cloud-solutions__benefit-icon">
                  <BenefitIcon />
                </div>
                <h3 className="cloud-solutions__benefit-title">{benefit.title}</h3>
                <p className="cloud-solutions__benefit-description">{benefit.description}</p>
              </motion.div>
            );
          })}
        </div>
      </section>

      {/* Process Section */}
      <section className="cloud-solutions__process">
        <div className="cloud-solutions__section-header">
          <motion.h2
            className="cloud-solutions__section-title"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
          >
            Our Cloud Implementation Process
          </motion.h2>
          <motion.p
            className="cloud-solutions__section-subtitle"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.1 }}
            viewport={{ once: true }}
          >
            A systematic approach to cloud transformation
          </motion.p>
        </div>
        <div className="cloud-solutions__process-timeline">
          {process.map((step, index) => (
            <motion.div
              key={step.step}
              className="cloud-solutions__process-step"
              initial={{ opacity: 0, x: index % 2 === 0 ? -50 : 50 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6, delay: index * 0.2 }}
              viewport={{ once: true }}
            >
              <div className="cloud-solutions__process-number">{step.step}</div>
              <div className="cloud-solutions__process-content">
                <h3 className="cloud-solutions__process-title">{step.title}</h3>
                <p className="cloud-solutions__process-description">{step.description}</p>
              </div>
            </motion.div>
          ))}
        </div>
      </section>

      {/* Testimonials Section */}
      <section className="cloud-solutions__testimonials">
        <div className="cloud-solutions__section-header">
          <motion.h2
            className="cloud-solutions__section-title"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
          >
            Client Success Stories
          </motion.h2>
        </div>
        <div className="cloud-solutions__testimonials-grid">
          {testimonials.map((testimonial, index) => (
            <motion.div
              key={testimonial.author}
              className="cloud-solutions__testimonial-card"
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: index * 0.1 }}
              viewport={{ once: true }}
            >
              <div className="cloud-solutions__testimonial-quote">
                <FaQuoteLeft />
              </div>
              <div className="cloud-solutions__testimonial-stars">
                {[...Array(testimonial.rating)].map((_, i) => (
                  <FaStar key={i} />
                ))}
              </div>
              <p className="cloud-solutions__testimonial-text">"{testimonial.text}"</p>
              <div className="cloud-solutions__testimonial-author">
                <strong>{testimonial.author}</strong>
                <span>{testimonial.company}</span>
              </div>
            </motion.div>
          ))}
        </div>
      </section>

      {/* CTA Section */}
      <section className="cloud-solutions__cta">
        <motion.div
          className="cloud-solutions__cta-content"
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          viewport={{ once: true }}
        >
          <h2 className="cloud-solutions__cta-title">Ready to Move to the Cloud?</h2>
          <p className="cloud-solutions__cta-text">
            Get a free cloud assessment and discover how cloud solutions can transform your business operations and reduce costs.
          </p>
          <div className="cloud-solutions__cta-buttons">
            <Link to="/contact" className="cloud-solutions__cta-btn cloud-solutions__cta-btn--primary">
              Get Free Assessment <FaArrowRight />
            </Link>
            <Link to="/portfolio" className="cloud-solutions__cta-btn cloud-solutions__cta-btn--secondary">
              View Case Studies
            </Link>
          </div>
        </motion.div>
      </section>

      {/* FAQ Section - SEO Boost */}
      <section className="cloud-solutions__faq">
        <div className="container">
          <motion.div
            className="cloud-solutions__section-header"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
          >
            <h2>Frequently Asked Questions About Cloud Solutions</h2>
            <p>Everything you need to know about cloud computing and migration</p>
          </motion.div>
          <div className="cloud-solutions__faq-list">
            <motion.div
              className="cloud-solutions__faq-item"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
              viewport={{ once: true }}
            >
              <h3>What are the benefits of moving to the cloud?</h3>
              <p>Cloud migration offers significant benefits including 30-50% cost savings, 99.99% uptime reliability, instant scalability, automatic backups, enhanced security, remote accessibility, and disaster recovery. You eliminate hardware costs, reduce IT overhead, improve collaboration, and gain flexibility to scale resources up or down based on demand.</p>
            </motion.div>
            <motion.div
              className="cloud-solutions__faq-item"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.1 }}
              viewport={{ once: true }}
            >
              <h3>Which cloud platform is best: AWS, Azure, or Google Cloud?</h3>
              <p>Each platform has strengths. AWS offers the most services and market maturity. Azure integrates seamlessly with Microsoft products. Google Cloud excels in data analytics and AI. We analyze your needs, existing infrastructure, budget, and technical requirements to recommend the optimal platform or multi-cloud strategy for your business.</p>
            </motion.div>
            <motion.div
              className="cloud-solutions__faq-item"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.2 }}
              viewport={{ once: true }}
            >
              <h3>How much does cloud migration cost?</h3>
              <p>Cloud migration costs vary by complexity. Simple migrations (&lt; 5 VMs) start at $1,000-$3,000, medium projects (5-10 VMs) range from $3,000-$6,000, and enterprise migrations (10+ VMs) cost $6,000-$10,000+. Ongoing cloud costs typically reduce by 30-50% compared to on-premise infrastructure. We provide detailed cost analysis and ROI projections.</p>
            </motion.div>
            <motion.div
              className="cloud-solutions__faq-item"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.3 }}
              viewport={{ once: true }}
            >
              <h3>How long does cloud migration take?</h3>
              <p>Migration timelines depend on complexity. Simple migrations take 1-2 weeks, medium complexity projects take 2-4 weeks, and large enterprise migrations take 4-8 weeks. We use phased approaches to minimize disruption, ensure zero downtime, and allow business continuity throughout the migration process.</p>
            </motion.div>
            <motion.div
              className="cloud-solutions__faq-item"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.4 }}
              viewport={{ once: true }}
            >
              <h3>Is my data secure in the cloud?</h3>
              <p>Yes! Cloud providers invest billions in security. We implement enterprise-grade security including encryption at rest and in transit, multi-factor authentication, role-based access control, network security, compliance certifications (SOC 2, ISO 27001, HIPAA), regular security audits, and 24/7 monitoring. Cloud security often exceeds on-premise capabilities.</p>
            </motion.div>
            <motion.div
              className="cloud-solutions__faq-item"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.5 }}
              viewport={{ once: true }}
            >
              <h3>Do you provide ongoing cloud management and support?</h3>
              <p>Absolutely! We offer comprehensive managed cloud services including 24/7 monitoring, cost optimization, security management, backup automation, performance tuning, scaling management, and technical support. Our managed services start at $2,000/month and ensure your cloud infrastructure runs optimally with maximum uptime and efficiency.</p>
            </motion.div>
          </div>
        </div>
      </section>
      </div>
    </>
  );
};

export default CloudSolutions;
